/* Приглашение клиентов — как в прототипе (ui.js, «БЛОК ПРИГЛАШЕНИЕ КЛИЕНТОВ»): один блок для профиля и окна
   «Пригласить клиента». Тренер отправляет не голую ссылку, а сообщение: из него клиент понимает, кто пишет и что
   делать (открыть в браузере телефона — иначе иконку на экран «Домой» не поставить). Текст — шаблон, тренер правит
   его под себя и сохраняет («Сохранить свой текст» → prefs.inviteText). Ссылку храним в тексте меткой {ссылка}:
   если ссылку когда-нибудь выпустят заново, сохранённый текст не устареет.
   InviteLink — короткое поле со ссылкой в пустых состояниях (нет клиентов). */
import { trpc } from '@tg/app-core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMe } from '../lib/data';
import { CLIENT_URL } from '../lib/dict';
import { toast } from './Toast';
import { Modal } from './Modal';

/** Ссылку скопировали или отправили — шаг «Пригласите клиента» в «Первых шагах» выполнен */
export function useInvited() {
  const utils = trpc.useUtils();
  const set = trpc.trainer.onbSet.useMutation({ onSuccess: () => utils.trainer.dashboard.invalidate() });
  const done = useRef(false);
  return () => {
    if (done.current) return;
    done.current = true;
    set.mutate({ invited: true });
  };
}

function useInviteLink() {
  const invite = trpc.trainer.invite.useMutation();
  useEffect(() => {
    invite.mutate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return invite.data ? CLIENT_URL + '/j/' + invite.data.code : '';
}

export function InviteLink() {
  const link = useInviteLink();
  const invited = useInvited();
  return (
    <div className="copyfield" style={{ maxWidth: 380, margin: '0 auto' }}>
      <code title={link}>
        <span>{link ? link.replace(/^https?:\/\//, '').replace(/[^/]+$/, '') : '…'}</span>
        <b>{link.split('/').pop()}</b>
      </code>
      <button
        className="btn gh sm"
        disabled={!link}
        onClick={() => {
          navigator.clipboard?.writeText(link);
          invited();
          toast('Ссылка скопирована');
        }}
      >
        Скопировать
      </button>
    </div>
  );
}

const LINK = '{ссылка}';
/* Тренера мигрировали из других приложений — слов «теперь всё будет здесь» в шаблоне нет */
const inviteDefault = (first: string) => `Привет! Это ${first || 'твой тренер'}. Зову тебя в Тренерграм — тут будут твои тренировки, а отговорка «забыл, что сегодня» больше не работает 😄

Как подключиться:
1. Открой ссылку в браузере телефона (Safari или Chrome, не из мессенджера):
${LINK}
2. Введи номер телефона — и ты у меня в списке
3. Чтобы иконка была на главном экране: «Поделиться» → «На экран Домой»`;

/** Текст растёт по содержимому: прокрутки внутри поля нет, видно всё сообщение */
const fit = (t: HTMLTextAreaElement | null) => {
  if (!t) return;
  t.style.height = 'auto';
  t.style.height = t.scrollHeight + 2 + 'px';
};

export function InviteBlock({ onCopied }: { onCopied?: () => void }) {
  const me = useMe().data;
  const link = useInviteLink();
  const invited = useInvited();
  const utils = trpc.useUtils();
  const upd = trpc.trainer.updateProfile.useMutation({ onSuccess: () => utils.trainer.me.invalidate() });
  const own = (me?.prefs as { inviteText?: string } | null)?.inviteText;
  const saved = (own ?? inviteDefault(me?.first || '')).split(LINK).join(link || '…');
  const [text, setText] = useState<string | null>(null);
  const val = text ?? saved;
  const dirty = text != null && text !== saved;
  const ta = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => fit(ta.current), [val]);
  /* Ширина поля меняется не только с окном (шрифт догрузился, окно открылось анимацией) — пересчёт по размеру */
  useEffect(() => {
    const t = ta.current;
    if (!t || typeof ResizeObserver === 'undefined') return;
    let w = 0;
    const ro = new ResizeObserver(([e]) => {
      if (Math.abs(e.contentRect.width - w) < 1) return;
      w = e.contentRect.width;
      fit(t);
    });
    ro.observe(t);
    /* Шрифт догрузился — строки переносятся иначе */
    document.fonts?.ready.then(() => fit(ta.current));
    return () => ro.disconnect();
  }, []);
  const copy = (s: string, msg: string) => {
    navigator.clipboard?.writeText(s);
    invited();
    onCopied?.();
    toast(msg);
  };
  return (
    <div className="invb">
      <textarea ref={ta} className="inp invb-t" rows={6} aria-label="Сообщение клиенту" spellCheck value={me && link ? val : ''} disabled={!me || !link} onChange={(e) => setText(e.target.value)} />
      <div className="invb-a">
        <button className="btn" disabled={!link} onClick={() => copy(val.trim(), 'Сообщение скопировано — вставьте его клиенту в мессенджер')}>
          Скопировать сообщение
        </button>
        <button className="btn gh" disabled={!link} onClick={() => copy(link, 'Ссылка скопирована')}>
          Скопировать ссылку
        </button>
        <button
          className="btn gh invb-save"
          disabled={!dirty || upd.isPending}
          onClick={() =>
            upd.mutate(
              { inviteText: link ? val.split(link).join(LINK) : val },
              {
                onSuccess: () => {
                  setText(null);
                  toast('Текст сохранён — теперь он будет вашим по умолчанию');
                },
                onError: (e) => toast(e.message || 'Не сохранилось'),
              },
            )
          }
        >
          {dirty ? 'Сохранить свой текст' : 'Сохранено'}
        </button>
      </div>
    </div>
  );
}

/* Окно «Пригласить клиента» со страницы «Клиенты» — тот же блок, что в профиле */
export function InviteModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Пригласить клиента" onClose={onClose}>
      <InviteBlock />
    </Modal>
  );
}
